/** SELECT FILE — AutoCAD's open dialog, over the drawings this database holds.
 *
 * ⚠ It shows the SOURCE file name beside the drawing name, because they differ and the
 * difference is the point: `views.py` records the extension as it ARRIVED. The old app labels
 * every CAD import `dwg` regardless, which is why its database cannot answer "how many DWGs have
 * we had" — the question that should have decided its whole DWG strategy.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { archiveDrawing, duplicateDrawing, importFile, listDrawings,
         listDrawingsIncludingArchived, purgeDrawing, renameDrawing, restoreDrawing,
         type DrawingRow } from "./api";

export default function OpenDialog({ rows, onPick, onClose, onChanged }: {
  rows: DrawingRow[];
  onPick: (r: DrawingRow) => void;
  onClose: () => void;
  /** ⚠ The studio owns the list; this dialog changes it and hands back a fresh one rather than
   *  keeping a second copy that would drift the moment a drawing is renamed. */
  onChanged?: (rows: DrawingRow[]) => void;
}) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<number | null>(rows[0]?.id ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [all, setAll] = useState<DrawingRow[]>(rows);
  //: The row being renamed, and the text so far. ⚠ Renaming IN THE LIST rather than in a dialog:
  //: a drafter renaming six sheets should not answer six modals.
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const file = useRef<HTMLInputElement | null>(null);

  useEffect(() => { setAll(rows); }, [rows]);

  const reload = async (archived = showArchived) => {
    const got = await (archived ? listDrawingsIncludingArchived() : listDrawings());
    setAll(got);
    // ⚠ The studio only ever wants the LIVE list; the archived view is this dialog's business.
    onChanged?.(archived ? got.filter((r) => !r.archived) : got);
    return got;
  };

  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    try { await fn(); await reload(); setBusy(null); }
    catch (e) { setBusy(String(e).replace(/^Error:\s*/, "")); }
  };

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    const hit = t ? all.filter((r) => (r.name + r.source_name).toLowerCase().includes(t)) : all;
    return [...hit].sort((a, b) => b.id - a.id);
  }, [all, q]);
  const current = list.find((r) => r.id === sel) ?? null;

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const go = () => { const r = list.find((x) => x.id === sel); if (r) onPick(r); };

  return (
    // ⚠⚠ **FLEX, NOT `display: grid` + `placeItems: center`.** In a grid the row track sizes
    // itself to the item, so the item's `maxHeight: 78%` resolves against a track that is
    // already as tall as the content — circular, and browsers give you the content size. The
    // dialog came out 1278 px tall in a 1000 px window and its button row was below the bottom
    // of the screen: Duplicate, Archive and Open all present and none of them reachable.
    // A flex container has a definite height, so a percentage against it means something.
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 24, zIndex: 90 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 720, maxHeight: "100%", display: "flex", flexDirection: "column",
                    background: "#3c3f41", border: "1px solid #555", borderRadius: 4,
                    boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          Select File
        </div>
        <div style={{ display: "flex", gap: 8, padding: "8px 12px", alignItems: "center" }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="File name"
                 onKeyDown={(e) => { if (e.key === "Enter") go(); }}
                 style={inp} />
          <label style={{ display: "flex", alignItems: "center", gap: 5, color: "#9aa0a6",
                          fontSize: 11, whiteSpace: "nowrap", cursor: "pointer" }}
                 title="Archived drawings are hidden, not deleted">
            <input type="checkbox" checked={showArchived} data-testid="open-archived"
                   onChange={(e) => { setShowArchived(e.target.checked);
                                      void reload(e.target.checked); }} />
            Archived
          </label>
          <button style={btn} onClick={() => file.current?.click()}>Import DWG/DXF…</button>
          <input ref={file} type="file" accept=".dwg,.dxf,.DWG,.DXF" style={{ display: "none" }}
                 onChange={async (e) => {
                   const f = e.target.files?.[0];
                   if (!f) return;
                   setBusy(`Reading ${f.name}…`);
                   try {
                     const { drawing, report } = await importFile(f);
                     setBusy(null);
                     // ⚠ The DRAWING as the API returned it, not a hand-built row. The two
                     // drift the moment a field is added, and the one that drifts is the one
                     // shown to whoever just imported a file.
                     if (report?.lost) setBusy(`⚠ ${report.lost} object(s) could not be read.`);
                     onPick(drawing as DrawingRow);
                   } catch (ex) { setBusy(`Could not read that file — ${ex}`); }
                 }} />
        </div>
        {/* ⚠⚠ **`minHeight: 0` OR THE DIALOG GROWS PAST ITS OWN maxHeight.** A flex child will
            not shrink below its CONTENT height without it, so a list of forty drawings pushed
            the dialog taller than the 78% cap and shoved the button row off the bottom of the
            screen — Duplicate, Archive and Open were all there and none of them reachable. The
            list is the thing that scrolls; it has to be allowed to be shorter than its rows. */}
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", margin: "0 12px",
                      border: "1px solid #555", background: "#2b2d2f" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#3c3f41", textAlign: "left" }}>
                <th style={th}>Name</th><th style={th}>Source file</th>
                <th style={{ ...th, textAlign: "right" }}>Objects</th>
                <th style={{ ...th, textAlign: "right" }}>Layers</th>
                <th style={{ ...th, width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} data-testid={`open-row-${r.id}`}
                    onClick={() => setSel(r.id)} onDoubleClick={() => onPick(r)}
                    style={{ cursor: "pointer",
                             background: sel === r.id ? "#2d5a8e" : "transparent" }}>
                  <td style={td}>
                    {editing?.id === r.id ? (
                      // ⚠ Renamed IN PLACE. A drafter renaming six sheets should not answer six
                      // modals, and the name is the only field that is editable anyway.
                      <input autoFocus value={editing.name} data-testid="open-rename"
                             onChange={(e) => setEditing({ id: r.id, name: e.target.value })}
                             onClick={(e) => e.stopPropagation()}
                             onKeyDown={(e) => {
                               if (e.key === "Enter") {
                                 const n = editing.name.trim();
                                 setEditing(null);
                                 if (n && n !== r.name) void act("Renaming…",
                                                                 () => renameDrawing(r.id, n));
                               } else if (e.key === "Escape") { e.stopPropagation(); setEditing(null); }
                             }}
                             onBlur={() => setEditing(null)}
                             style={{ ...inp, padding: "1px 4px", font: "11.5px system-ui" }} />
                    ) : (
                      <>
                        {r.name}
                        {/* ⚠ Said on the row, not only in the checkbox above: a list showing
                            archived drawings without marking them is a list you cannot trust. */}
                        {r.archived && <span style={{ color: "#c8a35a", fontSize: 10.5 }}>
                          {"  "}archived</span>}
                      </>
                    )}
                  </td>
                  <td style={{ ...td, color: "#9aa0a6" }}>{r.source_name}</td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {r.entities.toLocaleString()}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {r.layer_count}
                  </td>
                  <td style={{ ...td, textAlign: "center", cursor: "pointer", color: "#9aa0a6" }}
                      title="Rename" data-testid={`open-edit-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); setEditing({ id: r.id, name: r.name }); }}>
                    ✎
                  </td>
                </tr>
              ))}
              {!list.length && (
                <tr><td style={{ ...td, color: "#9aa0a6" }} colSpan={5}>
                  No drawing matches “{q}”.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12,
                      flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 150,
                         color: busy && /could not|error|Error/i.test(busy) ? "#f88" : "#9aa0a6",
                         fontSize: 11.5 }}>
            {busy ?? `${list.length} of ${all.length}`}
          </span>
          <button style={btn} disabled={!current} data-testid="open-duplicate"
                  title="A copy to work on, round-tripped through this app"
                  onClick={() => current && void act("Duplicating…", async () => {
                    await duplicateDrawing(current.id); })}>
            Duplicate
          </button>
          {current?.archived ? (
            <>
              <button style={btn} data-testid="open-restore"
                      onClick={() => void act("Restoring…", () => restoreDrawing(current.id))}>
                Restore
              </button>
              {/* ⚠⚠ THE ONE IRREVERSIBLE BUTTON IN THE APP, and it looks like it: red, only on
                  an already-archived drawing, and it names the drawing in the confirm. The
                  server refuses it for anyone who is not an administrator. */}
              <button data-testid="open-purge"
                      style={{ ...btn, background: "#5c1f1f", borderColor: "#a44" }}
                      onClick={() => {
                        if (!window.confirm(
                          `Permanently delete "${current.name}"?\n\n`
                          + "This cannot be undone. The drawing and every object in it are "
                          + "removed from the database.")) return;
                        void act("Deleting…", () => purgeDrawing(current.id));
                      }}>
                Delete for ever
              </button>
            </>
          ) : (
            <button style={btn} disabled={!current} data-testid="open-archive"
                    title="Hides it from this list. It is not deleted and can be restored."
                    onClick={() => current && void act("Archiving…",
                                                       () => archiveDrawing(current.id))}>
              Archive
            </button>
          )}
          <button data-testid="open-cancel" style={btn} onClick={onClose}>Cancel</button>
          <button data-testid="open-ok" style={{ ...btn, background: "#3c5a82", borderColor: "#4a6d9c" }}
                  onClick={go} disabled={sel == null}>Open</button>
        </div>
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  flex: 1, background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "4px 8px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
const th: React.CSSProperties = { padding: "5px 8px", borderBottom: "1px solid #555",
                                  fontWeight: 600 };
const td: React.CSSProperties = { padding: "4px 8px", borderBottom: "1px solid #35383a" };
